"use client";

import { motion } from "framer-motion";
import type { Experience } from "@/types/database";

type Props = {
    experiences: Experience[];
};

const containerVariants = {
    initial: {},
    animate: {
        transition: {
            staggerChildren: 0.2,
        },
    },
};

const itemVariants = {
    initial: { opacity: 0, x: -30 },
    animate: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: [0.2, 0, 0, 1] as const },
    },
};

export function ExperienceTimeline({ experiences }: Props) {
    if (!experiences.length) return null;

    return (
        <motion.div
            variants={containerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-50px" }}
            className="relative"
        >
            {/* Vertical line */}
            <div className="absolute left-[23px] top-3 bottom-3 w-px bg-gradient-to-b from-dev-blue via-dev-cyan to-transparent sm:left-[27px]" />

            <div className="space-y-6">
                {experiences.map((exp) => (
                    <motion.div
                        key={exp.id}
                        variants={itemVariants}
                        className="relative flex gap-5 sm:gap-6"
                    >
                        {/* Dot */}
                        <div className="relative z-10 mt-2 flex-shrink-0">
                            <div
                                className={`flex h-[14px] w-[14px] items-center justify-center rounded-full border-2 sm:h-[16px] sm:w-[16px] ${exp.is_current
                                        ? "border-dev-cyan bg-dev-cyan shadow-[0_0_16px_rgba(6,182,212,0.6)]"
                                        : "border-dev-text-muted bg-dev-bg-dark"
                                    }`}
                            >
                                {exp.is_current && (
                                    <span className="block h-1.5 w-1.5 animate-ping rounded-full bg-dev-cyan" />
                                )}
                            </div>
                        </div>

                        {/* Card */}
                        <div className="clay-card flex-1 p-5 sm:p-6">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <h3 className="text-base font-semibold text-dev-text-primary sm:text-lg">
                                        {exp.position}
                                    </h3>
                                    <p className="text-sm text-dev-cyan">{exp.company}</p>
                                </div>
                                <span
                                    className={`mt-1 inline-flex items-center gap-1.5 font-mono text-xs sm:mt-0 ${exp.is_current ? "text-dev-emerald" : "text-dev-text-muted"
                                        }`}
                                >
                                    {exp.is_current && (
                                        <span className="h-1.5 w-1.5 rounded-full bg-dev-emerald animate-pulse" />
                                    )}
                                    {exp.period_start} – {exp.period_end || "Present"}
                                </span>
                            </div>

                            <ul className="mt-4 space-y-2">
                                {exp.responsibilities.map((item, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-2 text-sm text-dev-text-secondary leading-relaxed"
                                    >
                                        <span className="mt-1.5 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-dev-blue/50" />
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-4 flex flex-wrap gap-2">
                                {exp.tech_stack.map((tech) => (
                                    <span key={tech} className="tech-tag">
                                        {tech}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
}
